/**
 * Macro calculation state object
 * @typedef {Object} MacroState
 * @property {number} protein - Protein in grams
 * @property {number} carbs - Carbs in grams
 * @property {number} fat - Fat in grams
 * @property {number} calorieGoal - Calorie goal
 * @property {number} [calorieFloor] - Calorie floor
 * @property {boolean} [proteinLocked] - Whether protein is locked
 * @property {boolean} [carbsLocked] - Whether carbs is locked
 * @property {boolean} [fatLocked] - Whether fat is locked
 * @property {boolean} [caloriesGoalLocked] - Whether calorie goal is locked
 * @property {Object} [lastValidMacros] - Last valid macro values {protein, carbs, fat}
 * @property {number} [weight] - User weight in kg
 * @property {number} [bodyFat] - Body fat percentage
 * @property {Object} [recommendedMacros] - Recommended macro values {protein, carbs, fat, calories, calorie_floor}
 */

// Calculate macro percentages
export const calculatePercentages = (proteinGrams, carbsGrams, fatGrams, calorieGoal) => {
  if (!calorieGoal || calorieGoal <= 0) {
    return {
      protein: 0,
      carbs: 0,
      fat: 0,
    };
  }

  let protein = Math.round(((proteinGrams * 4) / calorieGoal) * 100);
  let fat = Math.round(((fatGrams * 9) / calorieGoal) * 100);
  let carbs = 100 - protein - fat;

  protein = Math.max(0, Math.min(100, protein));
  fat = Math.max(0, Math.min(100, fat));
  carbs = Math.max(0, Math.min(100, carbs));

  const sum = protein + carbs + fat;
  if (sum !== 100) {
    carbs += 100 - sum;
    if (carbs < 0) {
      let overflow = Math.abs(carbs);
      carbs = 0;
      const fatReduction = Math.min(fat, overflow);
      fat -= fatReduction;
      overflow -= fatReduction;
      if (overflow > 0) {
        protein = Math.max(0, protein - overflow);
      }
    }
  }

  return {
    protein,
    carbs,
    fat,
  };
};

// Utility function to validate if a value is valid
export const isValidValue = (value) => {
  return (
    value !== null &&
    value !== undefined &&
    !isNaN(value) &&
    isFinite(value) &&
    value >= 0
  );
};

// Calculate minimum calorie floor based on protein, carbs, and 15% fat requirement
export const calculateMinCalorieFloor = (protein, carbs, recommendedMacros = null) => {
  const recommendedProtein = recommendedMacros?.protein;
  const proteinForFloor =
    recommendedProtein && recommendedProtein > 0
      ? Math.min(protein, recommendedProtein)
      : protein;
  const carbsForFloor = Math.min(carbs, 30);
  const proteinCalories = proteinForFloor * 4;
  const carbsCalories = carbsForFloor * 4;
  // Minimum = (protein + carbs) / 0.85 to ensure room for 15% fat
  return (proteinCalories + carbsCalories) / 0.85;
};

// Calculate fat floor (15% of total calories)
export const getFatFloor = (totalCalories) => {
  return (totalCalories * 0.15) / 9; // Convert calories to grams
};

const getLeanBodyMass = (weight, bodyFat = 0) => {
  if (weight && weight > 0) {
    return weight * (1 - bodyFat / 100);
  }
  return 0;
};

// Calculate protein floor (1.2g per kg of lean body mass)
export const getProteinFloor = (weight, bodyFat = 0) => {
  return getLeanBodyMass(weight, bodyFat) * 1.2;
};

// Calculate fat permyriad (fat percentage * 10000)
export const getFatPermyriad = (fatGrams, calorieGoal) => {
  if (calorieGoal <= 0) {
    return 0;
  }
  return Math.round(((fatGrams * 9) / calorieGoal) * 10000);
};

// Recalculate macros when calorie goal changes (maintains fat percentage)
export const recalculateMacrosForCalorieGoalChange = (state) => {
  const { protein: oldProtein, carbs: oldCarbs, fat: oldFat } = state;
  const newCalorieGoal = state.newCalorieGoal || state.calorieGoal;
  
  const calorieDiff =
    newCalorieGoal - (oldProtein * 4 + oldCarbs * 4 + oldFat * 9);

  if (Math.abs(calorieDiff) < 0.1) {
    return { protein: oldProtein, carbs: oldCarbs, fat: oldFat };
  }

  // Calculate current fat percentage to maintain it
  const oldProteinCal = oldProtein * 4;
  const oldCarbsCal = oldCarbs * 4;
  const oldFatCal = oldFat * 9;
  const oldTotalCal = oldProteinCal + oldCarbsCal + oldFatCal;
  const fatPercentage =
    oldTotalCal > 0 ? (oldFatCal / oldTotalCal) * 100 : 0;

  let newProtein = oldProtein;
  let newCarbs = oldCarbs;
  let newFat = oldFat;

  if (calorieDiff > 0) {
    // + Calorie Goal: Add to carbs and fat to maintain fat percentage
    const targetFatCal = (fatPercentage / 100) * newCalorieGoal;
    const targetFatGrams = targetFatCal / 9;
    const fatCaloriesToAdd = targetFatGrams * 9 - oldFatCal;
    const carbsCaloriesToAdd = calorieDiff - fatCaloriesToAdd;

    newFat = targetFatGrams;
    newCarbs = oldCarbs + carbsCaloriesToAdd / 4;
  } else {
    // - Calorie Goal: Remove from carbs and fat to maintain fat percentage
    const caloriesToTake = Math.abs(calorieDiff);
    const targetFatCal = (fatPercentage / 100) * newCalorieGoal;
    const targetFatGrams = targetFatCal / 9;
    const fatCaloriesToRemove = oldFatCal - targetFatCal;
    const carbsCaloriesToRemove = caloriesToTake - fatCaloriesToRemove;

    newFat = targetFatGrams;
    newCarbs = Math.max(0, oldCarbs - carbsCaloriesToRemove / 4);
  }

  return {
    protein: Math.max(0, newProtein),
    carbs: Math.max(0, newCarbs),
    fat: Math.max(0, newFat),
  };
};

// Recalculate macros when a macro changes
export const recalculateMacrosForMacroChange = (state) => {
  const {
    macro,
    newGrams,
    protein: currProtein,
    carbs: currCarbs,
    fat: currFat,
    proteinLocked = false,
    carbsLocked = false,
    fatLocked = false,
    caloriesGoalLocked = false,
    lastValidMacros
  } = state;

  let baseProtein = currProtein;
  let baseCarbs = currCarbs;
  let baseFat = currFat;

  if (caloriesGoalLocked) {
    // Use last committed valid macros as the baseline when calories are locked.
    // This matches native behavior and avoids keystroke-to-keystroke drift while typing.
    const hasValidBaseline =
      lastValidMacros &&
      isValidValue(lastValidMacros.protein) &&
      isValidValue(lastValidMacros.carbs) &&
      isValidValue(lastValidMacros.fat);

    if (hasValidBaseline) {
      baseProtein = lastValidMacros.protein;
      baseCarbs = lastValidMacros.carbs;
      baseFat = lastValidMacros.fat;
    } else if (
      !isValidValue(currProtein) ||
      !isValidValue(currCarbs) ||
      !isValidValue(currFat)
    ) {
      return {
        protein: macro === "protein" ? newGrams : currProtein,
        carbs: macro === "carbs" ? newGrams : currCarbs,
        fat: macro === "fat" ? newGrams : currFat,
      };
    }
  }

  const calsPerGram = {
    protein: 4,
    carbs: 4,
    fat: 9,
  };

  const locks = {
    protein: !!proteinLocked,
    carbs: !!carbsLocked,
    fat: !!fatLocked,
  };

  const getMacroValue = (macros, code) => macros[code];
  const setMacroValue = (macros, code, value) => ({ ...macros, [code]: value });
  const clampNonNegative = (value) => Math.max(0, value);

  const tryTakeCalories = (macros, code, desiredCalories) => {
    if (desiredCalories <= 0 || locks[code]) {
      return { macros, taken: 0 };
    }
    const available = clampNonNegative(getMacroValue(macros, code) * calsPerGram[code]);
    const taken = Math.min(desiredCalories, available);
    const nextValue = clampNonNegative(getMacroValue(macros, code) - taken / calsPerGram[code]);
    return {
      macros: setMacroValue(macros, code, nextValue),
      taken,
    };
  };

  const tryAddCalories = (macros, code, desiredCalories) => {
    if (desiredCalories <= 0 || locks[code]) {
      return { macros, added: 0 };
    }
    const added = desiredCalories;
    const nextValue = getMacroValue(macros, code) + added / calsPerGram[code];
    return {
      macros: setMacroValue(macros, code, nextValue),
      added,
    };
  };

  const baseMacros = {
    protein: clampNonNegative(baseProtein),
    carbs: clampNonNegative(baseCarbs),
    fat: clampNonNegative(baseFat),
  };

  const baseTotalCalories =
    baseMacros.protein * calsPerGram.protein +
    baseMacros.carbs * calsPerGram.carbs +
    baseMacros.fat * calsPerGram.fat;
  const baseFatPercentage = baseTotalCalories > 0 ? (baseMacros.fat * calsPerGram.fat) / baseTotalCalories : 0;

  const oldMacroCalories = baseMacros[macro] * calsPerGram[macro];
  const newMacroCalories = newGrams * calsPerGram[macro];
  let remainingDiffCalories = newMacroCalories - oldMacroCalories;

  let newMacros = {
    ...baseMacros,
    [macro]: clampNonNegative(newGrams),
  };

  if (macro === "protein") {
    if (remainingDiffCalories > 0) {
      const fromCarbs = tryTakeCalories(newMacros, "carbs", remainingDiffCalories);
      newMacros = fromCarbs.macros;
      remainingDiffCalories -= fromCarbs.taken;

      if (remainingDiffCalories > 0) {
        const fromFat = tryTakeCalories(newMacros, "fat", remainingDiffCalories);
        newMacros = fromFat.macros;
        remainingDiffCalories -= fromFat.taken;
      }
    } else if (remainingDiffCalories < 0) {
      const caloriesToAdd = Math.abs(remainingDiffCalories);
      const addFatCalories = caloriesToAdd * baseFatPercentage;
      const addCarbCalories = caloriesToAdd - addFatCalories;

      const toFat = tryAddCalories(newMacros, "fat", addFatCalories);
      newMacros = toFat.macros;
      const toCarbs = tryAddCalories(newMacros, "carbs", addCarbCalories);
      newMacros = toCarbs.macros;

      remainingDiffCalories += toFat.added + toCarbs.added;
    }
  } else if (macro === "carbs") {
    if (remainingDiffCalories > 0) {
      const fromFat = tryTakeCalories(newMacros, "fat", remainingDiffCalories);
      newMacros = fromFat.macros;
      remainingDiffCalories -= fromFat.taken;

      if (remainingDiffCalories > 0) {
        const fromProtein = tryTakeCalories(newMacros, "protein", remainingDiffCalories);
        newMacros = fromProtein.macros;
        remainingDiffCalories -= fromProtein.taken;
      }
    } else if (remainingDiffCalories < 0) {
      const toFat = tryAddCalories(newMacros, "fat", Math.abs(remainingDiffCalories));
      newMacros = toFat.macros;
      remainingDiffCalories += toFat.added;
    }
  } else if (macro === "fat") {
    if (remainingDiffCalories > 0) {
      const fromCarbs = tryTakeCalories(newMacros, "carbs", remainingDiffCalories);
      newMacros = fromCarbs.macros;
      remainingDiffCalories -= fromCarbs.taken;

      if (remainingDiffCalories > 0) {
        const fromProtein = tryTakeCalories(newMacros, "protein", remainingDiffCalories);
        newMacros = fromProtein.macros;
        remainingDiffCalories -= fromProtein.taken;
      }
    } else if (remainingDiffCalories < 0) {
      const toCarbs = tryAddCalories(newMacros, "carbs", Math.abs(remainingDiffCalories));
      newMacros = toCarbs.macros;
      remainingDiffCalories += toCarbs.added;

      if (remainingDiffCalories < 0) {
        const toProtein = tryAddCalories(newMacros, "protein", Math.abs(remainingDiffCalories));
        newMacros = toProtein.macros;
        remainingDiffCalories += toProtein.added;
      }
    }
  }

  // If calories are locked and redistribution can't fully absorb the change,
  // keep baseline values for non-edited macros and preserve the edited value.
  if (caloriesGoalLocked && Math.abs(remainingDiffCalories) > 1e-6) {
    newMacros = {
      ...baseMacros,
      [macro]: clampNonNegative(newGrams),
    };
  }

  return {
    protein: clampNonNegative(newMacros.protein),
    carbs: clampNonNegative(newMacros.carbs),
    fat: clampNonNegative(newMacros.fat),
  };
};

// Validate calorie floor
export const validateCalorieFloor = (state) => {
  const { calorieFloor: floor, protein, carbs, recommendedMacros } = state;
  const errors = [];
  const minCalorieFloor = calculateMinCalorieFloor(protein, carbs, recommendedMacros);

  if (floor < minCalorieFloor) {
    errors.push(
      `Calorie floor must be at least ${Math.round(
        minCalorieFloor
      )} calories (based on minimum protein, carbs, and 15% fat requirement)`
    );
  }

  return errors.length > 0 ? errors : null;
};

// Validate macros
export const validateMacros = (state) => {
  const {
    protein,
    carbs,
    fat,
    calorieGoal: calories,
    calorieFloor,
    caloriesGoalLocked = false,
    weight,
    bodyFat
  } = state;
  
  const errors = [];

  if (!isValidValue(protein) || !isValidValue(carbs) || !isValidValue(fat)) {
    errors.push("Error: Invalid macro values.");
  }
  // Calorie goal minimum is the calorie floor
  if (calories < calorieFloor) {
    if (calorieFloor > 0) {
      errors.push("Error: Calorie goal cannot be below calorie floor.");
    } else {
      errors.push("Error: Calorie goal must be greater than 0.");
    }
  }
  if (calories > 10000) {
    errors.push("Error: Calories exceed the upper safety limit (12,000)kcal.");
  }

  // Check if combined macro calories exceed or fall below calorie goal when locked
  // Allow 10 calorie tolerance to account for rounding
  if (
    caloriesGoalLocked &&
    isValidValue(protein) &&
    isValidValue(carbs) &&
    isValidValue(fat) &&
    calories > 0
  ) {
    const totalMacroCalories = protein * 4 + carbs * 4 + fat * 9;
    if (totalMacroCalories > calories + 10) {
      errors.push("Error: Calories from macros exceed calorie goal.");
    }
  }

  // Protein lower limit: 1.2g per kg of lean body mass
  if (weight && weight > 0) {
    const proteinLowerLimit = getProteinFloor(weight, bodyFat);
    if (protein < proteinLowerLimit) {
      errors.push(
        `Error: Protein below minimum of ${Math.round(proteinLowerLimit)}g.`
      );
    }
  }

  // Protein upper limit: 4g to 6g per kg of lean body mass based on body fat
  if (weight && weight > 0 && bodyFat !== null && bodyFat !== undefined) {
    const leanBodyMass = weight * (1 - bodyFat / 100);

    // Calculate upper limit: 4g at 10% BF, 6g at 65% BF (linear interpolation)
    let proteinUpperLimitPerKg;
    if (bodyFat < 10) {
      proteinUpperLimitPerKg = 4;
    } else if (bodyFat > 65) {
      proteinUpperLimitPerKg = 6;
    } else {
      // Linear interpolation: 4 + (bodyFat - 10) * (6 - 4) / (65 - 10)
      proteinUpperLimitPerKg = 4 + ((bodyFat - 10) * 2) / 55;
    }

    const proteinUpperLimit = leanBodyMass * proteinUpperLimitPerKg;
    if (protein > proteinUpperLimit) {
      errors.push(
        `Error: Protein must be below ${Math.round(proteinUpperLimit)}g.`
      );
    }
  }

  // Fat minimum: 15% of total calories
  if (calories > 0) {
    const fatMinimumCalories = calories * 0.15;
    const fatMinimumGrams = fatMinimumCalories / 9;
    if (fat < fatMinimumGrams) {
      errors.push(
        `Error: Fat must be above ${Math.round(fatMinimumGrams)}g (15% of calories).`
      );
    }
  }

  return errors.length > 0 ? errors : null;
};

// Validate high/low plan compatibility with new macros
export const validateHighLowPlan = (state, userAccount) => {
  const { protein, carbs: newCarbs, fat: newFat } = state;
  const errors = [];

  // Check if high_low_plan exists and is an array
  const highLowPlan = userAccount?.high_low_plan;
  if (!highLowPlan || !Array.isArray(highLowPlan) || highLowPlan.length === 0) {
    return null; // No high/low plan, so no validation needed
  }

  // Get current user's carbs and fat (use last_carbs/last_fat or carbs/fat)
  const currentCarbs = userAccount.last_carbs ?? userAccount.carbs ?? 0;
  const currentFat = userAccount.last_fat ?? userAccount.fat ?? 0;
  
  const roundedCarbs = Math.round(currentCarbs);
  const roundedFat = Math.round(currentFat);

  // Get protein (should be constant across all days)
  const userProtein = protein ?? userAccount.last_protein ?? userAccount.protein ?? 150;

  // Find the lowest carbs and fat across all high/low days
  let lowestCarbs = roundedCarbs;
  let lowestFat = roundedFat;
  let lowestDayCalories = userProtein * 4 + roundedCarbs * 4 + roundedFat * 9;

  highLowPlan.forEach((day) => {
    if (day && typeof day === 'object' && 'carbs' in day && 'fat' in day) {
      const thisDayCarbs = newCarbs + day.carbs - roundedCarbs;
      const thisDayFat = newFat + day.fat - roundedFat;
      
      // Calculate calories for this day
      const thisDayCalories = userProtein * 4 + thisDayCarbs * 4 + thisDayFat * 9;
      
      if (thisDayCarbs < lowestCarbs) {
        lowestCarbs = thisDayCarbs;
      }
      if (thisDayFat < lowestFat) {
        lowestFat = thisDayFat;
        lowestDayCalories = thisDayCalories;
      }
      // Also check if this day has lower calories (for fat minimum calculation)
      if (thisDayCalories < lowestDayCalories) {
        lowestDayCalories = thisDayCalories;
      }
    }
  });

  // Check minimums
  // For high/low plans, carbs can go down to 0 (no minimum carbs requirement)
  const MIN_FAT_PERCENTAGE = 0.15; // 15% of calories

  // Only check that carbs is not negative
  if (lowestCarbs < 0) {
    errors.push(
      `These macros would invalidate your high/low day plan. The lowest day would have ${Math.round(lowestCarbs)}g carbs, which is negative.`
    );
  }

  // Calculate minimum fat based on the lowest day's calories
  if (lowestDayCalories > 0) {
    const minFatGrams = (lowestDayCalories * MIN_FAT_PERCENTAGE) / 9;
    if (lowestFat < minFatGrams) {
      errors.push(
        `These macros would invalidate your high/low day plan. The lowest day would have ${Math.round(lowestFat)}g fat, which is below the minimum of ${Math.round(minFatGrams)}g (15% of calories).`
      );
    }
  }

  return errors.length > 0 ? errors : null;
};
